import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

const DAYS_OF_WEEK = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function AvailabilityConfig({ value = {}, onChange }) {
    // value format: { monday: [{start: "09:00", end: "17:00"}], tuesday: [] }

    const handleAddSlot = (day) => {
        const newSlots = [...(value[day] || []), { start: "09:00", end: "17:00" }];
        onChange({ ...value, [day]: newSlots });
    };

    const handleUpdateSlot = (day, index, field, val) => {
        const newSlots = [...(value[day] || [])];
        newSlots[index] = { ...newSlots[index], [field]: val };
        onChange({ ...value, [day]: newSlots });
    };

    const handleRemoveSlot = (day, index) => {
        const newSlots = [...(value[day] || [])];
        newSlots.splice(index, 1);
        onChange({ ...value, [day]: newSlots });
    };

    return (
        <div className="space-y-4">
            {DAYS_OF_WEEK.map((day) => {
                const slots = value[day] || [];
                const isActive = slots.length > 0;

                return (
                    <div key={day} className="flex flex-col sm:flex-row sm:items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="w-32 flex items-center justify-between sm:justify-start gap-2 pt-1">
                            <span className="capitalize font-medium text-slate-700">{day}</span>
                            <div
                                className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${isActive ? 'bg-emerald-500' : 'bg-slate-300'}`}
                                role="switch"
                                aria-checked={isActive}
                                aria-label={`Toggle availability for ${day}`}
                                tabIndex="0"
                                onClick={() => {
                                    if (isActive) {
                                        onChange({ ...value, [day]: [] });
                                    } else {
                                        handleAddSlot(day);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        if (isActive) {
                                            onChange({ ...value, [day]: [] });
                                        } else {
                                            handleAddSlot(day);
                                        }
                                    }
                                }}
                            >
                                <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${isActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
                            </div>
                        </div>

                        <div className="flex-1 space-y-2">
                            {isActive ? (
                                <>
                                    {slots.map((slot, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <input
                                                type="time"
                                                value={slot.start}
                                                onChange={(e) => handleUpdateSlot(day, index, "start", e.target.value)}
                                                className="p-1 px-2 border rounded border-slate-300 text-sm focus:outline-none focus:border-blue-500"
                                            />
                                            <span className="text-slate-400">-</span>
                                            <input
                                                type="time"
                                                value={slot.end}
                                                onChange={(e) => handleUpdateSlot(day, index, "end", e.target.value)}
                                                className="p-1 px-2 border rounded border-slate-300 text-sm focus:outline-none focus:border-blue-500"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveSlot(day, index)}
                                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8"
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </div>
                                    ))}
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleAddSlot(day)}
                                        className="text-blue-600 hover:text-blue-800 text-xs py-1 h-auto"
                                    >
                                        <Plus size={14} className="mr-1" /> Add Hours
                                    </Button>
                                </>
                            ) : (
                                <div className="text-slate-400 text-sm py-1 italic">Unavailable</div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

AvailabilityConfig.propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
};
